<template>
  <CenteredPageContainer :class="$style.page" wide>
    <div :class="[$style.page__main, $style.setting]">
      <SettingsPageSideMenu
        :contract="result?.contract ?? undefined"
        :viewer="viewer"
        :uiControl="uiControl"
      />
      <div>
        <router-view
          :class="$style.view__child"
          @onBeforeLeaveConfirmation="handleBeforeLeaveConfirmation"
        />
      </div>
    </div>

    <component :is="ConfirmationModal" @close="confirm(false)">
      <SettingsPageBeforeLeaveConfirmation
        @confirm="confirm(true)"
        @cancel="confirm(false)"
      />
    </component>
  </CenteredPageContainer>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useRoute } from 'vue-router'

import CenteredPageContainer from '@/components/molecules/CenteredPageContainer.vue'
import SettingsPageBeforeLeaveConfirmation from './SettingsPageBeforeLeaveConfirmation.vue'
import SettingsPageSideMenu from './SettingsPageSideMenu.vue'

import { useModal } from '@/hooks/useModal'

import { useSettingsPageQuery } from './SettingsPage.generated'

type InterruptedCallbacks = { onConfirm: () => void; onAbort: () => void }

const { result, refetch } = useSettingsPageQuery()
const route = useRoute('settings')

const viewer = computed(() => {
  return result.value?.viewer
})

const entranceGUID = computed(() => {
  return route.params.entranceGUID
})
watch(entranceGUID, () => refetch(), { immediate: true })

const uiControl = computed(() => {
  return result.value?.uiControl
})

const interruptedCallbacks = ref<Maybe<InterruptedCallbacks>>(undefined)
const { ConfirmationModal, openConfirmationModal, closeConfirmationModal } =
  useModal('confirmation')

const handleBeforeLeaveConfirmation = (callbacks: InterruptedCallbacks) => {
  interruptedCallbacks.value = callbacks
  openConfirmationModal()
}

const confirm = (confirmed: boolean) => {
  if (interruptedCallbacks.value === undefined) {
    closeConfirmationModal()
    return
  }

  ;(confirmed
    ? interruptedCallbacks.value.onConfirm
    : interruptedCallbacks.value.onAbort)()
  interruptedCallbacks.value = undefined
  closeConfirmationModal()
}
</script>

<style lang="scss" module>
.page {
  padding-bottom: 80px;

  &__header {
    &:nth-child(n + 2) {
      margin-top: 40px;
    }
  }

  &__main {
    &:nth-child(n + 2) {
      margin-top: 32px;
    }
  }
}

.setting {
  display: grid;
  align-items: flex-start;
  gap: 40px;
  grid-template-columns: 302px 1fr;
  grid-template-rows: auto;
}
</style>
